// The web app's shared query cache (TanStack Query). A page's server data lives under a key, so
// something other than the page (the chat panel, after a tool changed the design) can say "this is
// stale" and every page showing it reloads in place. Keys:
//   ['design', slug, branch, resource, ...]  a project branch's design (elements, sources, rules, ...)
//   ['design', slug, branch, 'agents', ...]  a branch's project agents (the list; one agent by key)
//   ['agent-runs', slug, ...]               the runs project agents made (the assistant ledger)
//   ['board', ...]                           Board cards
// Pages read through `useApiQuery`, which keeps the shape of the older `useLoad` (data, error,
// loading, reload), so moving a page over changes only how it loads.
import { QueryClient, useQuery, type QueryKey } from '@tanstack/react-query';
import * as React from 'react';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A failed load shows its error at once, as before; the page offers its own retry.
      retry: false,
      refetchOnWindowFocus: false,
      // Opening a page loads fresh data (as useLoad did); the cache is for sharing and invalidating.
      staleTime: 0,
    },
  },
});

/** The key of one design resource of a project branch (`extra` narrows it: a rule's test cases). */
export const designKey = (slug: string, branch: string, resource: string, ...extra: unknown[]) =>
  ['design', slug, branch, resource, ...extra] as const;

/** Every design resource of a project branch (what a chat edit of unknown reach invalidates). */
export const designScope = (slug: string, branch: string) => ['design', slug, branch] as const;

/**
 * A branch's project agents (the list; one agent by key) and the runs of a project's agents: the agents
 * SDK's own keys, under the scope the host binds (`designScope(slug, branch)` for agents, `[slug]` for runs).
 */
export { agentRunsKey, agentsKey } from '@cortex/sdk-agents-react';

export const boardKey = (...extra: unknown[]) => ['board', ...extra] as const;

export interface ApiQuery<T> {
  data: T | null;
  error: string | null;
  /** True while loading, the first time and on every reload. */
  loading: boolean;
  reload: () => void;
}

/** A cached load: the useLoad shape over the shared cache. */
export function useApiQuery<T>(
  key: QueryKey,
  fn: () => Promise<T>,
  opts: { enabled?: boolean; refetchInterval?: number | false } = {},
): ApiQuery<T> {
  const q = useQuery(
    {
      queryKey: key,
      queryFn: fn,
      ...(opts.enabled === undefined ? {} : { enabled: opts.enabled }),
      ...(opts.refetchInterval === undefined ? {} : { refetchInterval: opts.refetchInterval }),
    },
    queryClient,
  );
  const { refetch } = q;
  const reload = React.useCallback(() => void refetch(), [refetch]);
  return {
    data: q.data ?? null,
    error: q.error ? (q.error instanceof Error ? q.error.message : String(q.error)) : null,
    loading: q.isFetching || (q.isPending && opts.enabled !== false),
    reload,
  };
}

/** Marks queries stale: the ones on screen reload at once, the others on their next use. */
export function invalidate(keys: readonly QueryKey[]) {
  for (const queryKey of keys) void queryClient.invalidateQueries({ queryKey });
}
